import { twMerge } from "tailwind-merge";
import { Skeleton } from "../../Skeleton";
import type { CellComponentProps, CellEmphasis, LinkBehavior } from "../DataTable.types";
import { getCellContainerClasses, getCellTextClasses, getSkeletonSize, resolveValue, wrapWithLink } from "./cellUtils";

export interface DateCellProps<T = any> extends CellComponentProps<T> {
  format?: "date" | "time" | "datetime" | "relative" | "custom";
  customFormat?: Intl.DateTimeFormatOptions;
  emptyText?: string;
  align?: "left" | "center" | "right";
  emphasis?: CellEmphasis;
  href?: string | ((row: T) => string);
  linkBehavior?: LinkBehavior;
  className?: string;
}

/**
 * DateCell
 *
 * DataTable cell component for displaying date and time values.
 * Supports multiple formats including relative time, with optional linking capabilities.
 */
export function DateCell<T = any>({
  value,
  row,
  context,
  format = "date",
  customFormat,
  emptyText = "—",
  align = "left",
  emphasis = "normal",
  href,
  linkBehavior = "hover",
  className,
}: DateCellProps<T>) {
  const { isLoading } = context;

  if (isLoading) {
    return <Skeleton className={getSkeletonSize(emphasis)} />;
  }

  if (!value) {
    return <div className={getCellContainerClasses(align, "text-text-secondary")}>{emptyText}</div>;
  }

  const date = value instanceof Date ? value : new Date(value);

  if (isNaN(date.getTime())) {
    return <div className={getCellContainerClasses(align, "text-text-secondary")}>{emptyText}</div>;
  }

  let formattedValue: string;

  switch (format) {
    case "date":
      formattedValue = date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
      break;
    case "time":
      formattedValue = date.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
      });
      break;
    case "datetime":
      formattedValue = date.toLocaleString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
      break;
    case "relative":
      formattedValue = getRelativeTime(date);
      break;
    case "custom":
      formattedValue = customFormat ? date.toLocaleString("en-US", customFormat) : date.toISOString();
      break;
    default:
      formattedValue = date.toLocaleDateString();
  }

  const containerClasses = getCellContainerClasses(align, twMerge(getCellTextClasses(emphasis), className));

  // If href is provided, wrap with link behavior
  const resolvedHref = resolveValue(href, row);
  if (resolvedHref) {
    return <div className={containerClasses}>{wrapWithLink(formattedValue, resolvedHref, row, linkBehavior)}</div>;
  }

  return <div className={containerClasses}>{formattedValue}</div>;
}

function getRelativeTime(date: Date): string {
  const now = new Date();
  const diffInMs = now.getTime() - date.getTime();
  const diffInSecs = Math.floor(diffInMs / 1000);
  const diffInMins = Math.floor(diffInSecs / 60);
  const diffInHours = Math.floor(diffInMins / 60);
  const diffInDays = Math.floor(diffInHours / 24);

  if (diffInSecs < 60) {
    return "just now";
  } else if (diffInMins < 60) {
    return `${diffInMins} minute${diffInMins !== 1 ? "s" : ""} ago`;
  } else if (diffInHours < 24) {
    return `${diffInHours} hour${diffInHours !== 1 ? "s" : ""} ago`;
  } else if (diffInDays < 7) {
    return `${diffInDays} day${diffInDays !== 1 ? "s" : ""} ago`;
  } else {
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  }
}
